import { useCallback, useMemo } from 'react';
import { useAccessControl } from '@/features/access-control';
import {
  useDeleteMenuMutation,
  useDuplicateMenuMutation,
} from '@/features/organizations/api/menusApi';
import useSnackbar from '@/hooks/useSnackbar';
import { useNavigate, useParams } from 'react-router-dom';
import { Menu } from '@/types/menus';
import routes from '@/constants/routes';
import { ButtonProps, Stack, Button } from '@mui/material';
import { useFormikContext } from 'formik';
import ButtonGroup from '@/components/ButtonGroup';

interface Props {
  isLoading?: boolean;
  selectedMenu?: Menu;
  onClose: () => void;
}

const UpdateMenuActions = ({ selectedMenu, onClose, isLoading }: Props) => {
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const { organizationId } = useParams<{ organizationId: string }>();

  const [deleteMenu, deleteMenuMutation] = useDeleteMenuMutation();
  const [duplicateMenu, duplicateMenuMutation] = useDuplicateMenuMutation();

  const { values } = useFormikContext<Menu>();

  const handleMenuDeletion = useCallback(async () => {
    if (!values.id) return;

    const response = await deleteMenu({ menuId: values.id, organizationId });

    if (response?.error) {
      openSnackbar('Error al eliminar el menú', 'error');
    } else {
      openSnackbar('Menú eliminado', 'success');
      onClose();
    }
  }, [deleteMenu, onClose, openSnackbar, organizationId, values.id]);

  const handleDuplicateMenu = useCallback(async () => {
    const response = await duplicateMenu({
      menu: {
        ...values,
        title: values.title.trim() + ' (copia)',
      },
      organizationId,
    });

    if (response?.error) {
      openSnackbar('Error al crear una copia del menú', 'error');
    } else {
      openSnackbar('Copia realizada', 'success');
      onClose();
    }
  }, [duplicateMenu, onClose, openSnackbar, organizationId, values]);

  const openEditor = useCallback(() => {
    if (!selectedMenu?.id) return;

    navigate(
      routes.organizations.menus.edit
        .replace(':organizationId', organizationId ?? '')
        .replace(':menuId', selectedMenu.id)
    );
  }, [navigate, organizationId, selectedMenu?.id]);

  const actions = useMemo(() => {
    const draft = [];

    if (hasAccess('update.menu')) {
      draft.push({
        type: 'submit',
        color: 'success',
        children: 'Guardar',
        disabled: isLoading,
      });
    }

    if (hasAccess('duplicate.menu')) {
      draft.push({
        children: 'Duplicar',
        onClick: () => handleDuplicateMenu(),
        disabled: isLoading || duplicateMenuMutation.isLoading,
      });
    }

    if (hasAccess('delete.menu')) {
      draft.push({
        color: 'error',
        children: 'Eliminar',
        onClick: () => handleMenuDeletion(),
        disabled: isLoading || deleteMenuMutation.isLoading,
      });
    }

    if (hasAccess('design.menu')) {
      draft.push({
        children: 'Diseñar',
        onClick: openEditor,
      });
    }

    draft.push({
      children: 'Previsualizar',
      onClick: () => {
        if (!selectedMenu?.id) return;

        window.open(
          routes.organizations.menus.read
            .replace(':organizationId', organizationId ?? '')
            .replace(':menuId', selectedMenu.id),
          '_blank'
        );
      },
    });

    return draft as ButtonProps[];
  }, [
    deleteMenuMutation.isLoading,
    duplicateMenuMutation.isLoading,
    handleDuplicateMenu,
    handleMenuDeletion,
    hasAccess,
    isLoading,
    openEditor,
    organizationId,
    selectedMenu?.id,
  ]);

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <ButtonGroup actions={actions} />
      <Button disabled={isLoading} variant="text" onClick={onClose}>
        Cancelar
      </Button>
    </Stack>
  );
};

export default UpdateMenuActions;
